import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, AlertTriangle, RefreshCw, Info } from "lucide-react";
import { getSectionExplanation } from "../lib/api";

/**
 * SectionExplanationModal
 *
 * On-demand modal that requests a short discursive explanation for a single
 * section of the asset analysis page. Three states: loading / success / error.
 *
 * Race-condition safe:
 *   - each call uses its own AbortController
 *   - closing the modal or opening a different section cancels the in-flight
 *     request via `abort()` so a late response never overwrites the current
 *     modal state.
 */
export default function SectionExplanationModal({
    open,
    onClose,
    assetSymbol,
    sectionType,
    sectionLabel,
    sectionData,
}) {
    const [state, setState] = useState("idle"); // idle | loading | success | error
    const [text, setText] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const abortRef = useRef(null);

    const runFetch = () => {
        // Abort any previous in-flight request for a different section
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch { /* noop */ }
        }
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setState("loading");
        setErrMsg("");
        setText("");
        getSectionExplanation(
            { asset_symbol: assetSymbol, section_type: sectionType, section_data: sectionData || {} },
            ctrl.signal,
        )
            .then(res => {
                if (ctrl.signal.aborted) return;
                setText(res?.explanation || "");
                setState("success");
            })
            .catch(e => {
                if (ctrl.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
                const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
                setErrMsg(detail);
                setState("error");
            });
    };

    // Fire the request when the modal opens
    useEffect(() => {
        if (!open) return undefined;
        runFetch();
    }, [open, sectionType, assetSymbol]);

    // Cleanup on unmount / close
    useEffect(() => {
        if (!open && abortRef.current) {
            try { abortRef.current.abort(); } catch { /* noop */ }
            abortRef.current = null;
            setState("idle");
        }
    }, [open]);

    // Close on ESC
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            data-testid="section-explanation-modal"
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-elev border border-soft w-full sm:max-w-lg sm:rounded max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-soft" style={{ background: "rgba(59,130,246,0.08)" }}>
                    <Info size={14} style={{ color: "var(--brand-blue)" }} />
                    <div className="text-xs font-bold tracking-wider truncate" style={{ color: "var(--brand-blue)" }}>
                        {sectionLabel || "Spiegazione"}
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="section-modal-close"
                        className="ml-auto p-1 text-mute hover:text-foreground"
                        aria-label="Chiudi"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* body */}
                <div className="px-4 py-4 min-h-[120px] text-sm leading-relaxed">
                    {state === "loading" && (
                        <div data-testid="section-modal-loading" className="flex items-center gap-2 text-mute">
                            <Loader2 size={14} className="animate-spin" />
                            <span>Sto analizzando {sectionLabel || "la sezione"}…</span>
                        </div>
                    )}

                    {state === "success" && (
                        <div data-testid="section-modal-text" className="text-dim whitespace-pre-line">
                            {text}
                        </div>
                    )}

                    {state === "error" && (
                        <div data-testid="section-modal-error" className="text-xs">
                            <div className="flex items-start gap-2 mb-3" style={{ color: "var(--red)" }}>
                                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold">Non sono riuscito a generare la spiegazione</div>
                                    <div className="text-mute mt-0.5">{errMsg}</div>
                                </div>
                            </div>
                            <button
                                onClick={runFetch}
                                data-testid="section-modal-retry"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-soft text-xs tracking-wider hover:bg-elev2"
                                style={{ color: "var(--amber)" }}
                            >
                                <RefreshCw size={12} />
                                RIPROVA
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
